import { trigger } from "../../../helpers/routeHooks";
import { history } from "../../../client/history";
import {
  setIsRestoringFilters,
  setPageName,
  setPageLoading,
  setIsServerRendered,
} from "../../../state/modules/page/actions";
import clientSideRedirect from "../../../client/clientSideRedirect";
import { PAGE_NOT_FOUND } from "../../../state/modules/page/constants";
import getIsNewPage from "./getIsNewPage";
import getIsPageNumberChanged from "./getIsPageNumberChanged";
import getIsResultQueryChanged from "./getIsResultQueryChanged";
import matchRoutes from "../../../routes/matchRoutes";
import { clearCategoryError } from "../../../state/modules/category";
import getDataFetchingParameters from "./getDataFetchingParameters";
import { getCanonicalPath } from "../../../state/modules/router/selectors";
import { setupExperiments } from "../setupExperiments";
import { getFloorFiltersForNewSearch } from "../experimentFloorFilters";
import {
  getRestoredFiltersForNavigation,
  buildLocationWithRestoredFilters,
  buildReconciledSearch,
  buildValidatedRestoredLocation,
  buildLocationWithSearch,
  saveCurrentFiltersFromLocation,
  isSaveFiltersExperimentEnabled,
} from "../../../client/experimentSaveFilters";
import { setNewSearch } from "../../../state/modules/search";
import { getSuccessMessage } from "../../../state/modules/layout/selectors";
import {
  hideSuccessMessage,
  setDetectedFloor,
} from "../../../state/modules/layout/actions";
import { getIsLoadingMore } from "../../../state/modules/search/domains/pagination/selectors/getIsLoadingMore";
import { selectSearchIsPagingBackward } from "../../../state/modules/search/domains/ui/selectors";
import logger from "../../../lib/logger";
import { queryStringToObject } from "../../../helpers/queryStringToObject";

let shortCircuit = false;
let lastKnownGoodLocation;

const detectFloorFromUrl = (pathname) => {
  if (/^\/men(\/|$)/i.test(pathname)) return "men";
  if (/^\/women(\/|$)/i.test(pathname)) return "women";

  return null;
};

const shouldCheckPageNumberChange = ({
  navigationAction,
  isPaginatingInline,
}) => navigationAction !== "REPLACE" && !isPaginatingInline;

const isStaleFetch = (location) =>
  history.location.pathname !== location.pathname ||
  history.location.search !== location.search;

const runUnlessStale = (location, callback) =>
  isStaleFetch(location) ? null : callback();

const hasSamePageResultsSettled = (search, expectedQuery) =>
  Object.entries(expectedQuery).every(
    ([key, value]) => String(search?.query?.[key]) === String(value),
  ) &&
  (!expectedQuery.refine ? !search?.query?.refine : true) &&
  search?.isLoading !== true;

const hasNewPageResultsSettled = (categoryId, previousCategoryId, search) =>
  categoryId &&
  String(categoryId) !== String(previousCategoryId) &&
  String(search?.query?.cid) === String(categoryId) &&
  search?.isLoading !== true;

const shouldRecoverFromZeroResults = ({
  shouldRetry,
  restoredFilters,
  isNewPage,
  state,
}) =>
  Boolean(
    shouldRetry &&
    restoredFilters &&
    isNewPage &&
    state.category?.details?.id &&
    state.search?.itemCount === 0 &&
    !state.error,
  );

export const waitForResultsSettled = ({
  store,
  previousCategoryId,
  isNewPage,
  expectedLocation,
}) => {
  const expectedQuery = queryStringToObject(expectedLocation?.search || "");
  const hasExpectedQuery = Object.keys(expectedQuery).length > 0;

  if (
    !isSaveFiltersExperimentEnabled() ||
    typeof store.subscribe !== "function" ||
    (isNewPage ? !previousCategoryId : !hasExpectedQuery)
  ) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let unsubscribe = () => {};

    const finish = () => {
      clearTimeout(timeoutId);
      unsubscribe();
      resolve();
    };
    const hasSettled = () => {
      const { category, search } = store.getState();
      const categoryId = category?.details?.id;

      return isNewPage
        ? hasNewPageResultsSettled(categoryId, previousCategoryId, search)
        : hasSamePageResultsSettled(search, expectedQuery);
    };
    const timeoutId = setTimeout(finish, 2000);

    unsubscribe = store.subscribe(() => {
      if (hasSettled()) {
        finish();
      }
    });

    if (hasSettled()) {
      finish();

      return;
    }
  });
};

// The search API ignores filters (and filter values) the visited PLP does not
// support, so the fetched facets reflect only what was actually applied. This
// rewrites the URL to keep only the selected refine values (and to use the
// canonical category path) so the address bar matches the results.
const buildReconciledLocation = ({
  state,
  effectiveLocation,
  lastKnownGoodPathname,
  facets,
  isCategoryPage,
}) => {
  const reconciledSearch = isSaveFiltersExperimentEnabled()
    ? buildReconciledSearch(effectiveLocation.search, facets)
    : effectiveLocation.search;
  const searchChanged = reconciledSearch !== effectiveLocation.search;

  const canonicalPath = isCategoryPage ? getCanonicalPath(state) : null;
  const pathnameChanged =
    isCategoryPage && lastKnownGoodPathname !== canonicalPath;

  if (!pathnameChanged && !searchChanged) {
    return null;
  }

  const reconciledLocation = {
    ...effectiveLocation,
    ...(pathnameChanged ? { pathname: canonicalPath } : {}),
  };

  return searchChanged
    ? buildLocationWithSearch(reconciledLocation, reconciledSearch)
    : reconciledLocation;
};

const setupRouteChangeListener = (store, decisions) => {
  lastKnownGoodLocation = history.location;
  const initialFloor = detectFloorFromUrl(history.location.pathname);
  if (initialFloor) {
    store.dispatch(setDetectedFloor(initialFloor));
  }
  setupExperiments(store, decisions, history.location);
  history.listen((location, navigationAction) => {
    if (shortCircuit || location.isPrefilterGenderedSearch) {
      return;
    }

    const { pathname, search: query } = location;

    const { dispatch } = store;
    const detectedFloor = detectFloorFromUrl(pathname);
    if (detectedFloor) {
      dispatch(setDetectedFloor(detectedFloor));
    }

    const activeRoute = matchRoutes({ pathname });

    if (activeRoute.key === PAGE_NOT_FOUND) {
      clientSideRedirect({ response: { redirectUrl: `${pathname}${query}` } });
    } else if (activeRoute.key) {
      const { dispatch, getState } = store;
      const state = getState();
      const { category, search, page } = state;
      const catId = category?.details?.id;
      const searchTerm = search?.searchTerm;
      const currentPageNumber = parseInt(search?.query?.page, 10) || 1;
      const currentQuery = lastKnownGoodLocation?.search;
      const isPaginatingInline =
        getIsLoadingMore(state) || selectSearchIsPagingBackward(state);
      const { isVisible: isSuccessMessageVisible } = getSuccessMessage(state);

      const isNewPage = getIsNewPage({
        query,
        catId,
        searchTerm,
        page,
      });
      const isPageNumberChanged =
        shouldCheckPageNumberChange({
          navigationAction,
          isPaginatingInline,
        }) &&
        getIsPageNumberChanged({
          query,
          currentPageNumber,
        });
      const isResultQueryChanged =
        navigationAction !== "REPLACE" &&
        getIsResultQueryChanged({
          currentQuery,
          nextQuery: query,
        });
      const shouldFetchNewResults =
        isNewPage || isPageNumberChanged || isResultQueryChanged;

      activeRoute.props.component.load().then((loadedComponent) => {
        const currentLocation = history.location;
        if (
          currentLocation.pathname !== location.pathname ||
          currentLocation.search !== location.search
        ) {
          return;
        }

        let effectiveLocation = location;

        const floorFilters = getFloorFiltersForNewSearch(
          store,
          effectiveLocation,
        );
        if (floorFilters) {
          const searchParams = new URLSearchParams(effectiveLocation.search);
          searchParams.append("refine", floorFilters.refine);
          const modifiedLocation = {
            ...effectiveLocation,
            search: `?${searchParams.toString()}`,
            isPrefilterGenderedSearch: true,
          };
          history.replace(modifiedLocation);
          effectiveLocation = modifiedLocation;
        }

        const preRestoreLocation = buildLocationWithSearch(
          effectiveLocation,
          effectiveLocation.search,
        );
        const restoredFilters = isNewPage
          ? getRestoredFiltersForNavigation(effectiveLocation)
          : null;
        if (restoredFilters) {
          dispatch(setIsRestoringFilters(true));
          effectiveLocation = buildLocationWithRestoredFilters(
            effectiveLocation,
            restoredFilters,
          );
          dispatch(setNewSearch());
          shortCircuit = true;
          history.replace(effectiveLocation);
          shortCircuit = false;
        }

        const dataFetchingParameters = getDataFetchingParameters({
          store,
          location: effectiveLocation,
          dispatch,
          isNewPage,
          lastKnownGoodLocation,
          category,
          decisions,
        });

        if (shouldFetchNewResults) {
          dispatch(setNewSearch());
          dispatch(setPageLoading(true));
        }

        if (isNewPage) {
          dispatch(clearCategoryError());
          dispatch(setPageName(activeRoute.key));
          setupExperiments(store, decisions, effectiveLocation);

          if (isSuccessMessageVisible) {
            dispatch(hideSuccessMessage());
          }
        }

        dispatch(setIsServerRendered(false));

        const { pathname: lastKnownGoodPathname } = lastKnownGoodLocation;

        const fetchResults = (fetchingParameters, resultsAreNewPage) =>
          trigger("fetch", loadedComponent.default, fetchingParameters).then(
            () =>
              waitForResultsSettled({
                store,
                previousCategoryId: catId,
                isNewPage: resultsAreNewPage,
                expectedLocation: fetchingParameters.location,
              }),
          );

        const fetchAndReconcile = (
          fetchingParameters,
          shouldRecover = true,
          shouldRetryReconciledSearch = true,
        ) =>
          fetchResults(fetchingParameters, isNewPage && shouldRecover).then(
            () =>
              runUnlessStale(effectiveLocation, () => {
                const state = getState();
                const { category, error } = state;
                const facets = state.search?.facets ?? [];

                if (
                  shouldRecoverFromZeroResults({
                    shouldRetry: shouldRecover,
                    restoredFilters,
                    isNewPage,
                    state,
                  })
                ) {
                  const cleanFetchingParameters = {
                    ...fetchingParameters,
                    location: preRestoreLocation,
                  };

                  shortCircuit = true;
                  effectiveLocation = preRestoreLocation;
                  history.replace(effectiveLocation);
                  shortCircuit = false;
                  dispatch(setNewSearch());

                  return fetchResults(cleanFetchingParameters, false).then(
                    () => {
                      if (
                        history.location.pathname !==
                          effectiveLocation.pathname ||
                        history.location.search !== effectiveLocation.search
                      ) {
                        return null;
                      }

                      const cleanState = getState();
                      const cleanFacets = cleanState.search?.facets ?? [];
                      const validatedLocation = buildValidatedRestoredLocation(
                        preRestoreLocation,
                        cleanFacets,
                      );

                      if (!validatedLocation) {
                        shortCircuit = true;
                        effectiveLocation = preRestoreLocation;
                        history.replace(effectiveLocation);
                        shortCircuit = false;
                        lastKnownGoodLocation = effectiveLocation;
                        saveCurrentFiltersFromLocation(
                          effectiveLocation,
                          cleanFacets,
                        );

                        return null;
                      }

                      shortCircuit = true;
                      effectiveLocation = validatedLocation;
                      history.replace(effectiveLocation);
                      shortCircuit = false;

                      dispatch(setNewSearch());

                      return fetchAndReconcile(
                        {
                          ...fetchingParameters,
                          location: validatedLocation,
                        },
                        false,
                        true,
                      );
                    },
                  );
                }

                if (error) {
                  shortCircuit = true;
                  effectiveLocation = lastKnownGoodLocation;
                  history.replace(lastKnownGoodLocation);
                } else {
                  const reconciledLocation = buildReconciledLocation({
                    state,
                    effectiveLocation,
                    lastKnownGoodPathname,
                    facets,
                    isCategoryPage: Boolean(category?.details?.id),
                  });

                  if (reconciledLocation) {
                    shortCircuit = true;
                    effectiveLocation = reconciledLocation;
                    history.replace(effectiveLocation);

                    if (
                      shouldRetryReconciledSearch &&
                      reconciledLocation.search !==
                        fetchingParameters.location.search
                    ) {
                      shortCircuit = false;

                      return fetchAndReconcile(
                        {
                          ...fetchingParameters,
                          location: reconciledLocation,
                        },
                        false,
                        false,
                      );
                    }
                  }
                }
                shortCircuit = false;
                lastKnownGoodLocation = effectiveLocation;
                saveCurrentFiltersFromLocation(effectiveLocation, facets);
              }),
          );

        fetchAndReconcile(dataFetchingParameters)
          .catch((error) => {
            logger.error(error);
            dispatch(setPageLoading(false));
          })
          .finally(() => {
            if (restoredFilters) {
              dispatch(setIsRestoringFilters(false));
            }
          });
      });
    }
  });

  const initialRestoredFilters = getRestoredFiltersForNavigation(
    history.location,
  );
  if (initialRestoredFilters) {
    const restoredLocation = buildLocationWithRestoredFilters(
      history.location,
      initialRestoredFilters,
    );
    store.dispatch(setNewSearch());
    history.replace(restoredLocation);
  }
};

export default setupRouteChangeListener;
